/**
 * Employee Management API Service
 * Handles all employee-related API calls
 */

import { apiClient } from './client';
import { EmployeeRole } from '@repo/types';
import type { Employee, EmployeeStatus } from '@repo/types';

export interface EmployeeFilters {
  role?: EmployeeRole;
  status?: EmployeeStatus;
  teamId?: string;
  search?: string;
}

export interface PaginatedEmployeesResponse {
  data: Employee[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateEmployeeData {
  email: string;
  phone: string;
  password: string;
  name: string;
  role: EmployeeRole;
  teamId?: string;
}

export interface UpdateEmployeeData {
  name?: string;
  role?: EmployeeRole;
  status?: EmployeeStatus;
  teamId?: string | null;
}

/**
 * Employee API service class
 */
export class EmployeeApiService {
  /**
   * Get all employees with pagination and filters
   */
  async getEmployees(filters: EmployeeFilters = {}, page = 1, limit = 20) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    if (filters.role) params.append('role', filters.role);
    if (filters.status) params.append('status', filters.status);
    if (filters.teamId) params.append('teamId', filters.teamId);
    if (filters.search) params.append('search', filters.search);

    return apiClient.get<PaginatedEmployeesResponse>(`/employees?${params.toString()}`);
  }

  /**
   * Get employee by ID
   */
  async getEmployeeById(id: string) {
    return apiClient.get<Employee>(`/employees/${id}`);
  }

  /**
   * Create a new employee
   */
  async createEmployee(data: CreateEmployeeData) {
    return apiClient.post<Employee>('/auth/register', data);
  }

  /**
   * Update an existing employee
   */
  async updateEmployee(id: string, data: UpdateEmployeeData) {
    return apiClient.put<Employee>(`/employees/${id}`, data);
  }

  /**
   * Deactivate an employee
   */
  async deactivateEmployee(id: string) {
    return apiClient.patch<Employee>(`/employees/${id}/deactivate`, {});
  }

  /**
   * Get eligible team leads (team_lead, manager, or admin roles)
   */
  async getEligibleTeamLeads() {
    const response = await this.getEmployees({}, 1, 100);
    const employees = response.data.data;
    return employees.filter(
      (emp) =>
        emp.role === EmployeeRole.TeamLead ||
        emp.role === EmployeeRole.Manager ||
        emp.role === EmployeeRole.Admin
    );
  }
}

// Export singleton instance
export const employeeApi = new EmployeeApiService();